import { parseServerMessage, REFUSED_CLOSE_CODE, type ServerMessage } from "@app/protocol";

/**
 * The socket's decisions, apart from the socket (review 0027 C): what a frame means, and what to
 * do when the connection closes. Pure, so they can be tested without a browser.
 */

export type Heard =
  | { kind: "message"; message: ServerMessage }
  /** The server said something this build cannot read: a newer protocol, a game it does not know. */
  | { kind: "stale" }
  | { kind: "noise" };

function hasEnvelope(raw: unknown): boolean {
  return typeof raw === "object" && raw !== null && !Array.isArray(raw) && "type" in raw;
}

export function read(data: string): Heard {
  let raw: unknown = null;
  try {
    raw = JSON.parse(data);
  } catch {
    return { kind: "noise" };
  }
  const message = parseServerMessage(raw);
  if (message !== null) return { kind: "message", message };
  return hasEnvelope(raw) ? { kind: "stale" } : { kind: "noise" };
}

export interface Retry {
  /** Failures in a row; a socket that opens starts over. */
  attempt: number;
  /** When the server last closed the table normally. */
  lastNormalAt: number | null;
}

/** The table will not have us (its spectators are full): asking again would only be refused again. */
export const REFUSED_CODE = REFUSED_CLOSE_CODE;

export const FRESH: Retry = { attempt: 0, lastNormalAt: null };
/** Failures in a row before the table counts as unreachable: a new session is tried, or the player is told. */
export const GIVE_UP_AFTER = 3;
const MAX_DELAY_MS = 8_000;
/** Two normal closes this close together are a loop, not two table changes. */
const NORMAL_LOOP_MS = 5_000;

export function opened(retry: Retry): Retry {
  return { ...retry, attempt: 0 };
}

export interface Closed {
  retry: Retry;
  delay: number;
  state: "connecting" | "closed";
  /** True exactly once, on the failure that reaches `GIVE_UP_AFTER`. */
  unreachable: boolean;
  /** The table turned us away: do not reconnect, go somewhere else. */
  refused: boolean;
}

export function afterClose(retry: Retry, code: number, now: number): Closed {
  if (code === REFUSED_CODE)
    return { retry, delay: 0, state: "closed", unreachable: false, refused: true };
  const looping = retry.lastNormalAt !== null && now - retry.lastNormalAt < NORMAL_LOOP_MS;
  // The server closed the table normally (last player out): the channel's table is back at once.
  if (code === 1000 && !looping) {
    return {
      retry: { ...retry, lastNormalAt: now },
      delay: 0,
      state: "connecting",
      unreachable: false,
      refused: false,
    };
  }
  const attempt = retry.attempt + 1;
  return {
    retry: { ...retry, attempt },
    delay: Math.min(MAX_DELAY_MS, 1_000 * 2 ** retry.attempt),
    state: "closed",
    unreachable: attempt === GIVE_UP_AFTER,
    refused: false,
  };
}
